import { useTranslation } from "react-i18next";
import { ArrowRightLeft, BellRing, CalendarClock, CirclePlus, CreditCard, type LucideIcon } from "lucide-react";

import {
    SessionEvent,
    SessionEventActor,
    SessionProgressStatus,
    wallClockToDate,
} from "../../../domain/sessionEvent";

/** Looked up when rendering, not at import: the module is re-exported
 * widely, and an icon set stubbed in some other screen's tests must not
 * break loading it. */
function iconFor(type: SessionEvent["type"]): LucideIcon {
    switch (type) {
        case "created":
            return CirclePlus;
        case "status_changed":
            return ArrowRightLeft;
        case "payment_status_changed":
            return CreditCard;
        case "rescheduled":
            return CalendarClock;
        case "notification":
            return BellRing;
    }
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** A cancellation the patient asked for is named as the status picker names it. */
function progressLabel(status: SessionProgressStatus, cancelledBy: SessionEventActor | null, t: Translate): string {
    return status === "cancelled" && cancelledBy === "patient"
        ? t("reports.cancel.requestedByPatient")
        : t(`session.progressOptions.${status}`);
}

function describe(event: SessionEvent, t: Translate, formatStart: (value: string) => string): string {
    switch (event.type) {
        case "created":
            if (event.origin === "booking") return t("session.events.createdFromBooking");
            if (event.origin === "import") return t("session.events.createdByImport");
            return t("session.events.createdDirectly");
        case "status_changed":
            return t("session.events.statusChanged", {
                from: progressLabel(event.from, event.previouslyCancelledBy, t),
                to: progressLabel(event.to, event.cancelledBy, t),
            });
        case "payment_status_changed":
            return t("session.events.paymentStatusChanged", {
                from: t(`session.paymentOptions.${event.from}`),
                to: t(`session.paymentOptions.${event.to}`),
            });
        case "rescheduled":
            return t("session.events.rescheduled", {
                from: formatStart(event.fromStart),
                to: formatStart(event.toStart),
            });
        case "notification":
            return t(`session.events.notification.${event.outcome}`, {
                kind: t(`session.events.kind.${event.kind}`),
                channel: t(`session.events.channel.${event.channel}`),
                recipient: t(`session.events.recipient.${event.recipient}`),
            });
    }
}

/** The line under the description: who did it, and anything it adds. */
function detail(event: SessionEvent, t: Translate): string {
    const parts: string[] = [];
    if (event.type === "created") {
        parts.push(t("session.events.initialStatus", { status: t(`session.progressOptions.${event.initialStatus}`) }));
    }
    if (event.type === "payment_status_changed") {
        parts.push(t(`session.events.paymentSource.${event.source}`));
    }
    if (event.type === "notification" && event.recipientCount > 1) {
        parts.push(t("session.events.recipientCount", { count: event.recipientCount }));
    }
    if (event.type === "notification" && event.outcome === "opened") {
        // The therapist's own WhatsApp: say who, and that it may not have gone.
        parts.push(t(`session.events.actor.${event.actor ?? "therapist"}`), t("session.events.openedHint"));
    } else if (event.type !== "notification") {
        parts.push(t(`session.events.actor.${event.actor ?? "system"}`));
    }
    return parts.join(" · ");
}

interface SessionEventTimelineProps {
    events: SessionEvent[];
}

/** A session's history, oldest first. Presentation only. */
export default function SessionEventTimeline({ events }: SessionEventTimelineProps) {
    const { t, i18n } = useTranslation();
    const moment = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
    const formatStart = (value: string) => {
        const start = wallClockToDate(value);
        return start ? moment.format(start) : value;
    };

    return (
        <ol aria-label={t("session.events.listLabel")} className="relative space-y-4 border-l border-gray-200 dark:border-white/10 pl-6">
            {events.map((event) => {
                const Icon = iconFor(event.type);
                const failed = event.type === "notification" && event.outcome === "failed";
                return (
                    <li key={event.id} className="relative">
                        <span
                            aria-hidden="true"
                            className={`absolute -left-[2.1rem] top-0.5 flex h-6 w-6 items-center justify-center rounded-full ${
                                failed
                                    ? "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400"
                                    : "bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300"
                            }`}
                        >
                            <Icon className="h-3.5 w-3.5" />
                        </span>
                        <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                            <p className={`text-sm font-medium ${failed ? "text-red-700 dark:text-red-400" : "text-content-primary"}`}>
                                {describe(event, t, formatStart)}
                            </p>
                            <time dateTime={event.occurredAt.toISOString()} className="shrink-0 text-xs text-content-tertiary tabular-nums">
                                {moment.format(event.occurredAt)}
                            </time>
                        </div>
                        {detail(event, t) && <p className="text-xs text-content-tertiary">{detail(event, t)}</p>}
                    </li>
                );
            })}
        </ol>
    );
}
